import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import { API_BASE } from '../config';
import './AdminDashboard.css';

const TABS = [
  { key: 'overview',  label: 'Overview',     icon: '📊' },
  { key: 'users',     label: 'Users',        icon: '👤' },
  { key: 'priests',   label: 'Priests',      icon: '🕉️' },
  { key: 'kundali',   label: 'Horoscopes',   icon: '🔮' },
  { key: 'analytics', label: 'Analytics',    icon: '📈' },
  { key: 'maint',     label: 'Maintenance',  icon: '🛠️' },
];

export default function AdminDashboard() {
  const { admin, logout, adminFetch } = useAdminAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    if (!admin) navigate('/admin/login', { replace: true });
  }, [admin, navigate]);

  if (!admin) return null;

  return (
    <div className="admin-dashboard">
      <aside className="ad-sidebar">
        <div className="ad-brand">
          <span className="ad-brand-icon">🛡️</span>
          <div>
            <div className="ad-brand-title">AstroA2Z</div>
            <div className="ad-brand-sub">Super Admin</div>
          </div>
        </div>

        <div className="ad-admin-card">
          <div className="ad-avatar">{(admin.name || admin.email || 'A')[0].toUpperCase()}</div>
          <div>
            <div className="ad-admin-name">{admin.name || 'Admin'}</div>
            <div className="ad-admin-role">{admin.role}</div>
          </div>
        </div>

        <nav className="ad-nav">
          {TABS.map(t => (
            <button
              key={t.key}
              className={`ad-nav-item ${tab === t.key ? 'active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              <span className="ad-nav-icon">{t.icon}</span>
              <span>{t.label}</span>
            </button>
          ))}
        </nav>

        <button className="ad-logout" onClick={logout}>↩ Sign out</button>
      </aside>

      <main className="ad-main">
        {tab === 'overview'  && <OverviewTab  adminFetch={adminFetch} />}
        {tab === 'users'     && <UsersTab     adminFetch={adminFetch} />}
        {tab === 'priests'   && <PriestsTab   adminFetch={adminFetch} />}
        {tab === 'kundali'   && <KundaliTab   adminFetch={adminFetch} />}
        {tab === 'analytics' && <AnalyticsTab adminFetch={adminFetch} />}
        {tab === 'maint'     && <MaintenanceTab />}
      </main>
    </div>
  );
}

// ── Overview ────────────────────────────────────────────────────────────────
function OverviewTab({ adminFetch }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    adminFetch('/api/admin/overview').then(setData).catch(e => setErr(e.message));
  }, [adminFetch]);

  if (err) return <div className="ad-error">{err}</div>;
  if (!data) return <div className="ad-loading">Loading overview…</div>;

  return (
    <div>
      <h1 className="ad-page-title">Dashboard Overview</h1>
      <p className="ad-page-sub">Live snapshot of your AstroA2Z platform.</p>

      <div className="ad-stat-grid">
        <StatCard label="Total Users"      value={data.users.total}      sub={`+${data.users.today} today · ${data.users.last7days} this week`} accent="purple" />
        <StatCard label="Total Priests"    value={data.priests.total}    sub={`${data.priests.verified} verified · +${data.priests.today} today`} accent="gold" />
        <StatCard label="Kundalis Created" value={data.kundali.total}    sub={`+${data.kundali.today} today · ${data.kundali.last7days} this week`} accent="saffron" />
        <StatCard label="Saved Readings"   value={data.savedReadings}    sub="all-time" accent="maroon" />
      </div>

      <h2 className="ad-section-title">Priest Activity</h2>
      <div className="ad-stat-grid">
        <StatCard label="Bookings"       value={data.priestActivity.bookings}      sub={`${data.priestActivity.bookingsToday} today`} accent="gold" />
        <StatCard label="Muhurtas Saved" value={data.priestActivity.muhurtas}      accent="purple" />
        <StatCard label="Patrikas"       value={data.priestActivity.patrikas}      accent="saffron" />
        <StatCard label="Invoices"       value={data.priestActivity.invoices}      accent="maroon" />
      </div>

      <p className="ad-muted">Generated at {new Date(data.generatedAt).toLocaleString()}</p>
    </div>
  );
}

function StatCard({ label, value, sub, accent = 'purple' }) {
  return (
    <div className={`ad-stat-card ad-accent-${accent}`}>
      <div className="ad-stat-label">{label}</div>
      <div className="ad-stat-value">{Number(value || 0).toLocaleString()}</div>
      {sub && <div className="ad-stat-sub">{sub}</div>}
    </div>
  );
}

// ── Users ───────────────────────────────────────────────────────────────────
function UsersTab({ adminFetch }) {
  const [q, setQ] = useState('');
  const [data, setData] = useState({ users: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminFetch(`/api/admin/users?q=${encodeURIComponent(q)}&limit=100`);
      setData(d);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleActive = async (user) => {
    const updated = { isActive: !user.is_active };
    await adminFetch(`/api/admin/users/${user.id}`, { method: 'PUT', body: JSON.stringify(updated) });
    load();
  };

  return (
    <div>
      <h1 className="ad-page-title">Users <span className="ad-count">{data.total}</span></h1>
      <div className="ad-toolbar">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name or email…" onKeyDown={e => e.key === 'Enter' && load()} />
        <button onClick={load} className="ad-btn">Search</button>
      </div>
      {loading ? <div className="ad-loading">Loading…</div> : (
        <div className="ad-table-wrap">
          <table className="ad-table">
            <thead><tr><th>#</th><th>Name</th><th>Email</th><th>DOB</th><th>Place</th><th>Zodiac</th><th>Joined</th><th>Status</th></tr></thead>
            <tbody>
              {data.users.length === 0 && <tr><td colSpan="8" className="ad-empty">No users</td></tr>}
              {data.users.map(u => (
                <tr key={u.id}>
                  <td>{u.id}</td>
                  <td>{u.name || '—'}</td>
                  <td>{u.email}</td>
                  <td>{u.date_of_birth || '—'}</td>
                  <td>{u.place_of_birth || '—'}</td>
                  <td>{u.zodiac_sign || '—'}</td>
                  <td>{u.created_at?.slice(0, 10)}</td>
                  <td><button className={`ad-toggle ${u.is_active ? 'on' : 'off'}`} onClick={() => toggleActive(u)}>{u.is_active ? '✓ Active' : '✗ Disabled'}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Priests ─────────────────────────────────────────────────────────────────
function PriestsTab({ adminFetch }) {
  const [q, setQ] = useState('');
  const [data, setData] = useState({ priests: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminFetch(`/api/admin/priests?q=${encodeURIComponent(q)}&limit=100`);
      setData(d);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggle = async (p, field) => {
    const updated = { [field]: !p[field === 'isVerified' ? 'is_verified' : 'is_active'] };
    await adminFetch(`/api/admin/priests/${p.id}`, { method: 'PUT', body: JSON.stringify(updated) });
    load();
  };

  return (
    <div>
      <h1 className="ad-page-title">Priests <span className="ad-count">{data.total}</span></h1>
      <div className="ad-toolbar">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, email, phone, gotram…" onKeyDown={e => e.key === 'Enter' && load()} />
        <button onClick={load} className="ad-btn">Search</button>
      </div>
      {loading ? <div className="ad-loading">Loading…</div> : (
        <div className="ad-table-wrap">
          <table className="ad-table">
            <thead><tr><th>#</th><th>Name</th><th>Email</th><th>Phone</th><th>Gotram</th><th>Specialization</th><th>Exp</th><th>Verified</th><th>Active</th></tr></thead>
            <tbody>
              {data.priests.length === 0 && <tr><td colSpan="9" className="ad-empty">No priests</td></tr>}
              {data.priests.map(p => (
                <tr key={p.id}>
                  <td>{p.id}</td>
                  <td>{p.name}</td>
                  <td>{p.email}</td>
                  <td>{p.phone || '—'}</td>
                  <td>{p.gotram || '—'}</td>
                  <td>{p.specialization || '—'}</td>
                  <td>{p.experience_years || 0}y</td>
                  <td><button className={`ad-toggle ${p.is_verified ? 'on' : 'off'}`} onClick={() => toggle(p, 'isVerified')}>{p.is_verified ? '✓' : '✗'}</button></td>
                  <td><button className={`ad-toggle ${p.is_active ? 'on' : 'off'}`} onClick={() => toggle(p, 'isActive')}>{p.is_active ? '✓' : '✗'}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Kundali logs ────────────────────────────────────────────────────────────
function KundaliTab({ adminFetch }) {
  const [q, setQ] = useState('');
  const [data, setData] = useState({ logs: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminFetch(`/api/admin/kundali-logs?q=${encodeURIComponent(q)}&limit=100`);
      setData(d);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div>
      <h1 className="ad-page-title">Horoscope / Kundali Logs <span className="ad-count">{data.total}</span></h1>
      <div className="ad-toolbar">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name, email, place…" onKeyDown={e => e.key === 'Enter' && load()} />
        <button onClick={load} className="ad-btn">Search</button>
      </div>
      {loading ? <div className="ad-loading">Loading…</div> : (
        <div className="ad-table-wrap">
          <table className="ad-table">
            <thead><tr><th>#</th><th>Name</th><th>Email</th><th>DOB</th><th>TOB</th><th>Place</th><th>Gender</th><th>Device</th><th>When</th></tr></thead>
            <tbody>
              {data.logs.length === 0 && <tr><td colSpan="9" className="ad-empty">No logs</td></tr>}
              {data.logs.map(k => (
                <tr key={k.id}>
                  <td>{k.id}</td>
                  <td>{k.name || '—'}</td>
                  <td>{k.email || '—'}</td>
                  <td>{k.date_of_birth || '—'}</td>
                  <td>{k.time_of_birth || '—'}</td>
                  <td>{k.place_of_birth || '—'}</td>
                  <td>{k.gender || '—'}</td>
                  <td>{k.device_type || '—'}</td>
                  <td>{k.created_at?.replace('T', ' ').slice(0, 16)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Analytics ───────────────────────────────────────────────────────────────
function AnalyticsTab({ adminFetch }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    adminFetch('/api/admin/analytics').then(setData).catch(e => setErr(e.message));
  }, [adminFetch]);

  if (err) return <div className="ad-error">{err}</div>;
  if (!data) return <div className="ad-loading">Loading analytics…</div>;

  return (
    <div>
      <h1 className="ad-page-title">Analytics — last 30 days</h1>

      <h2 className="ad-section-title">New users / day</h2>
      <Sparkline series={data.usersLast30Days} color="#a855f7" />

      <h2 className="ad-section-title">New priests / day</h2>
      <Sparkline series={data.priestsLast30Days} color="#d4a017" />

      <h2 className="ad-section-title">Kundalis generated / day</h2>
      <Sparkline series={data.kundaliLast30Days} color="#ff9933" />

      <h2 className="ad-section-title">Priest bookings / day</h2>
      <Sparkline series={data.bookingsLast30Days} color="#9c1f1f" />

      <h2 className="ad-section-title">Device breakdown (kundali)</h2>
      <div className="ad-devices">
        {data.devices.length === 0 && <div className="ad-empty">No data</div>}
        {data.devices.map(d => (
          <div key={d.device} className="ad-device-row">
            <span>{d.device}</span>
            <span className="ad-device-bar"><span style={{ width: `${Math.min(100, d.c)}%` }} /></span>
            <strong>{d.c}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function Sparkline({ series, color = '#a855f7' }) {
  const points = useMemo(() => {
    if (!series?.length) return null;
    const max = Math.max(...series.map(s => Number(s.c) || 0), 1);
    const w = 720, h = 80, step = w / Math.max(series.length - 1, 1);
    return series.map((s, i) => {
      const x = i * step;
      const y = h - ((Number(s.c) || 0) / max) * (h - 8) - 4;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
  }, [series]);

  if (!points) return <div className="ad-empty">No data in this range</div>;
  return (
    <svg viewBox="0 0 720 80" className="ad-spark" preserveAspectRatio="none">
      <polyline fill="none" stroke={color} strokeWidth="2" points={points} />
    </svg>
  );
}

// ── Maintenance ─────────────────────────────────────────────────────────────
function MaintenanceTab() {
  return (
    <div>
      <h1 className="ad-page-title">Maintenance</h1>
      <p className="ad-page-sub">Server-side tools. Requires a separate admin sign-in on the PHP page.</p>

      <div className="ad-maint-grid">
        <a href="/php-apis/update-app.php" target="_blank" rel="noreferrer" className="ad-maint-card">
          <div className="ad-maint-icon">🛠️</div>
          <div className="ad-maint-title">Database Updater</div>
          <div className="ad-maint-sub">Apply pending schema changes (priest tables, admin tables, future migrations).</div>
        </a>
        <a href="/php-apis/db-check.php" target="_blank" rel="noreferrer" className="ad-maint-card">
          <div className="ad-maint-icon">🔌</div>
          <div className="ad-maint-title">DB Connectivity Check</div>
          <div className="ad-maint-sub">Verify the PHP API can connect to MySQL.</div>
        </a>
        <a href={`${API_BASE}/api/health`} target="_blank" rel="noreferrer" className="ad-maint-card">
          <div className="ad-maint-icon">❤️</div>
          <div className="ad-maint-title">API Health</div>
          <div className="ad-maint-sub">Ping the API and view version info.</div>
        </a>
      </div>
    </div>
  );
}
